// @ts-check
/**
 * Stardust global behaviour: dialogs, toasts, header state, scroll reveal and
 * ambient-animation pausing. Ported from tallowlove-stardust/src/scripts/app.ts.
 * Everything is delegated or re-scanned on theme-editor section loads, so
 * sections can be added, removed and re-rendered without a page reload.
 */

const root = document.documentElement;
export const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {any} */
export const config = /** @type {any} */ (window).Stardust || { routes: {}, strings: {} };

// Liquid's `t` filter HTML-escapes plain strings ("isn&#39;t"); decode them once so they
// read correctly when inserted as text. Markup in *_html strings is left as is.
{
  const decoder = document.createElement('textarea');
  for (const [key, value] of Object.entries(config.strings || {})) {
    decoder.innerHTML = String(value);
    config.strings[key] = decoder.value;
  }
}

/**
 * @template {Element} [T=HTMLElement]
 * @param {string} sel
 * @param {ParentNode} [scope]
 * @returns {T | null}
 */
export const $ = (sel, scope = document) => /** @type {T | null} */ (scope.querySelector(sel));

/**
 * @template {Element} [T=HTMLElement]
 * @param {string} sel
 * @param {ParentNode} [scope]
 * @returns {T[]}
 */
export const $$ = (sel, scope = document) => /** @type {T[]} */ (Array.from(scope.querySelectorAll(sel)));

/** @param {string} s */
export const esc = (s) =>
  s.replace(/[&<>"']/g, (ch) => /** @type {Record<string, string>} */ ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

/**
 * Formats cents with the shop's money format (e.g. "${{amount}}" → "$32.00").
 * @param {number} cents
 */
export function money(cents) {
  const format = String(config.moneyFormat || '${{amount}}');
  const value = cents / 100;
  /** @param {number} n @param {number} d @param {string} t @param {string} s */
  const fmt = (n, d, t, s) => {
    const [int, dec] = n.toFixed(d).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (dec ? s + dec : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(value, 2, "'", '.');
      default:
        return fmt(value, 2, ',', '.');
    }
  });
}

/**
 * Replaces [placeholder] tokens in a translated string.
 * @param {string} str
 * @param {Record<string, string | number>} vars
 */
export function tpl(str, vars) {
  return String(str).replace(/\[(\w+)\]/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

/* ------------------------------------------------------------------ toasts */

/**
 * @param {string} message
 * @param {{ label: string, run: () => void }} [action]
 */
export function toast(message, action) {
  const toaster = $('[data-toaster]');
  if (!toaster) return;
  // A modal dialog makes the rest of the page inert, so toasts must live inside the topmost open dialog.
  const host = $$('dialog[open]').at(-1) ?? document.body;
  if (toaster.parentElement !== host) host.append(toaster);
  const el = document.createElement('div');
  el.className = 'toast';
  el.innerHTML = `<span>${esc(message)}</span>`;
  if (action) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'toast__action';
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      action.run();
      el.remove();
    });
    el.append(btn);
  }
  toaster.append(el);
  setTimeout(() => el.classList.add('is-leaving'), 4200);
  setTimeout(() => el.remove(), 4600);
}

/* ------------------------------------------------------------------ dialogs */

/** @param {HTMLDialogElement | null | undefined} d */
export function openDialog(d) {
  if (!d || d.open) return;
  d.classList.remove('is-closing');
  d.showModal();
  root.classList.add('has-modal');
  d.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
}

/**
 * @param {HTMLDialogElement | null | undefined} d
 * @param {{ instant?: boolean }} [opts]
 */
export function closeDialog(d, opts = {}) {
  if (!d || !d.open) return;
  const finish = () => {
    d.classList.remove('is-closing');
    d.close();
    if (!$$('dialog[open]').length) root.classList.remove('has-modal');
  };
  if (reduceMotion || opts.instant) return finish();
  d.classList.add('is-closing');
  setTimeout(finish, 220);
}

// Click on the backdrop (the dialog element itself) or any [data-close] closes it.
document.addEventListener('click', (e) => {
  const target = /** @type {HTMLElement} */ (e.target);
  if (target instanceof HTMLDialogElement) return closeDialog(target);
  const closer = target.closest('[data-close]');
  if (closer) closeDialog(/** @type {HTMLDialogElement | null} */ (closer.closest('dialog')));
});
// `cancel` (Esc) does not bubble: listen in the capture phase.
document.addEventListener(
  'cancel',
  (e) => {
    if (!(e.target instanceof HTMLDialogElement)) return;
    e.preventDefault();
    closeDialog(e.target);
  },
  true,
);
// Native close (e.g. form method=dialog) keeps the page scroll lock in sync.
document.addEventListener(
  'close',
  () => {
    if (!$$('dialog[open]').length) root.classList.remove('has-modal');
  },
  true,
);

/* ------------------------------------------------------------------ mobile menu */

document.addEventListener('click', (e) => {
  const opener = /** @type {HTMLElement} */ (e.target).closest('[data-open-menu]');
  if (opener) openDialog(/** @type {HTMLDialogElement | null} */ ($('#menu')));
});

/* ------------------------------------------------------------------ header scroll state */

let ticking = false;
const onScrollHeader = () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    root.classList.toggle('is-scrolled', scrollY > 12);
    ticking = false;
  });
};
addEventListener('scroll', onScrollHeader, { passive: true });
onScrollHeader();

/* ------------------------------------------------------------------ reveal + ambient */

const revealIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            en.target.classList.add('is-in');
            revealIO?.unobserve(en.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
      )
    : null;

const ambientIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const en of entries) en.target.classList.toggle('is-paused', !en.isIntersecting);
      })
    : null;

/** @param {ParentNode} scope */
export function scan(scope = document) {
  for (const el of $$('[data-reveal]:not(.is-in)', scope)) {
    if (revealIO) revealIO.observe(el);
    else el.classList.add('is-in');
  }
  // Pause decorative loops (e.g. starfield twinkle) when off-screen.
  for (const el of $$('[data-ambient]', scope)) ambientIO?.observe(el);
}
scan();

/* ------------------------------------------------------------------ theme editor */

document.addEventListener('shopify:section:load', (e) => scan(/** @type {HTMLElement} */ (e.target)));
